import type { FunctionComponent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFeatureFlag } from '@trello/feature-flag-client';

import { noop } from 'app/src/noop';

type Tier = 1 | 2 | 3 | 4 | 5 | 6;

const MAX_COLLAPSE_TIERS = 6;
const BUFFER = 30;

export type ResponsiveBoardHeaderComponent =
  | 'BoardVisibilityButton'
  | 'ButlerBoardButtons'
  | 'PowerupsDirectoryButton'
  | 'PowerupBoardButton'
  | 'AutomationDirectoryButton'
  | 'FilterPopoverButton'
  | 'ViewSwitcher'
  | 'Facepile'
  | 'LeftHandNav'
  | 'SubscribedButton';

const RESPONSIVE_BOARD_HEADER_TIERS: Record<
  ResponsiveBoardHeaderComponent,
  Tier
> = {
  BoardVisibilityButton: 1,
  PowerupsDirectoryButton: 2,
  AutomationDirectoryButton: 2,
  Facepile: 3,
  ViewSwitcher: 4,
  FilterPopoverButton: 5,
  SubscribedButton: 5,
  ButlerBoardButtons: 6,
  PowerupBoardButton: 6,
  LeftHandNav: 6,
};

export interface ResponsiveBoardHeader {
  contextLoaded: boolean;

  /**
   * For a given header component, returns whether that component should render
   * in its collapsed (true) or expanded (false) state.
   */
  isComponentCollapsed: (component: ResponsiveBoardHeaderComponent) => boolean;

  /** Ref for the div wrapping the entire header. */
  headerRef: React.RefObject<HTMLDivElement>;

  /** Ref for the span wrapping the left-aligned part of the header. */
  leftAlignedSpanRef: React.RefObject<HTMLSpanElement>;

  /** Ref for the span wrapping the right-aligned part of the header. */
  rightAlignedSpanRef: React.RefObject<HTMLSpanElement>;

  /** Listens for `WidthObserver` changes to the entire header. */
  observeHeaderWidth: (width: number) => void;

  /** Listens for `WidthObserver` changes to the left span of the header. */
  observeLeftSpanWidth: (width: number) => void;

  /** Listens for `WidthObserver` changes to the right span of the header. */
  observeRightSpanWidth: (width: number) => void;

  /**
   * The value of `collapseTier` at which we consider the header
   * totally-collapsed.
   */
  maxCollapseTiers: number;

  /**
   * The number of collapsed states the header has attempted to cycle through
   * in order to reach a state where the left- and right-aligned spans don't
   * overlap. 0 means every component is expanded.
   */
  collapseTier: number;

  /**
   * True if the header has collapsed every component, and can now wrap to a
   * second line.
   */
  totallyCollapsed: boolean;
}

export const ResponsiveBoardHeaderContext = React.createContext<ResponsiveBoardHeader>(
  {
    contextLoaded: false,
    isComponentCollapsed: () => false,
    headerRef: React.createRef<HTMLDivElement>(),
    leftAlignedSpanRef: React.createRef<HTMLSpanElement>(),
    rightAlignedSpanRef: React.createRef<HTMLSpanElement>(),
    observeHeaderWidth: noop,
    observeLeftSpanWidth: noop,
    observeRightSpanWidth: noop,
    maxCollapseTiers: 0,
    collapseTier: 0,
    totallyCollapsed: false,
  },
);

export const ResponsiveBoardHeaderProvider: FunctionComponent = ({
  children,
}) => {
  const [collapseTier, setCollapseTier] = useState(0);
  const [headerWidth, setHeaderWidth] = useState(0);

  const headerRef = useRef<HTMLDivElement>(null);
  const leftAlignedSpanRef = useRef<HTMLSpanElement>(null);
  const rightAlignedSpanRef = useRef<HTMLSpanElement>(null);

  const [leftSpanWidth, setLeftSpanWidth] = useState(0);
  const [rightSpanWidth, setRightSpanWidth] = useState(0);

  const responsiveHeaderFlag = useFeatureFlag(
    'panorama.responsive-board-header',
    false,
  );

  // These two functions are triggered by the width of the right- or
  // left-aligned spans in the header changing.
  const setObservedLeft = useCallback((width: number) => {
    setLeftSpanWidth(width);
    setCollapseTier(0);
  }, []);

  const setObservedRight = useCallback((width: number) => {
    setRightSpanWidth(width);
    setCollapseTier(0);
  }, []);

  // This is triggered by the width of the entire header changing (via
  // WidthObserver).
  const setObservedWidth = useCallback((width: number) => {
    setHeaderWidth(width);
    setCollapseTier(0);
  }, []);

  // This useEffect runs when the header width or either span width changes, and
  // iteratively increases the collapse tier while the header is still wrapping.
  useEffect(() => {
    const leftRefWidth = leftAlignedSpanRef.current?.clientWidth ?? 0;
    const rightRefWidth = rightAlignedSpanRef.current?.clientWidth ?? 0;

    if (
      headerWidth > 0 &&
      // Proxy for "is the header wrapping"
      leftRefWidth + rightRefWidth >= headerWidth - BUFFER &&
      // And can we still collapse more elements
      collapseTier < MAX_COLLAPSE_TIERS
    ) {
      setCollapseTier(collapseTier + 1);
    }
  }, [collapseTier, headerWidth, leftSpanWidth, rightSpanWidth]);

  const isComponentCollapsed = useCallback(
    (component: ResponsiveBoardHeaderComponent) => {
      if (!responsiveHeaderFlag) return false;

      return collapseTier >= RESPONSIVE_BOARD_HEADER_TIERS[component];
    },
    [collapseTier, responsiveHeaderFlag],
  );

  return (
    <ResponsiveBoardHeaderContext.Provider
      value={{
        contextLoaded: true,
        isComponentCollapsed,
        headerRef,
        observeHeaderWidth: setObservedWidth,
        observeLeftSpanWidth: setObservedLeft,
        observeRightSpanWidth: setObservedRight,
        leftAlignedSpanRef,
        rightAlignedSpanRef,
        collapseTier: responsiveHeaderFlag ? collapseTier : 0,
        maxCollapseTiers: MAX_COLLAPSE_TIERS,
        totallyCollapsed:
          !responsiveHeaderFlag || collapseTier === MAX_COLLAPSE_TIERS,
      }}
    >
      {children}
    </ResponsiveBoardHeaderContext.Provider>
  );
};
